import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import useArrowNavigation from '../Hooks/useArrowNavigation';

const CustomSelect = ({
  label,
  srLabel,
  options,
  selectedItem,
  onChange,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const maxIndex = options.length - 1;

  const { highlightedIndex, setHighlightedIndex, resetIndex, handleKeyDown } =
    useArrowNavigation(maxIndex, {
      isOpen,
      selectionKeys: ['Enter', ' '],
      onItemSelect: (item) => {
        onChange(item);
        setIsOpen(false);
        resetIndex();
      },
      onClose: () => {
        setIsOpen(false);
        resetIndex();
      },
      getItemByIndex: (index) => options[index] || 0,
    });

  const toggleDropdown = () => setIsOpen(!isOpen);

  useEffect(() => {
    isOpen && dropdownRef.current?.focus();
  }, [isOpen]);

  const defaultOption = options.find(
    (option) => option.key === selectedItem.key
  );

  if (!options) return null;

  return (
    <div className="c-custom-select">
      {label && (
        <label className="c-custom-select__label" htmlFor="c-custom-select">
          {label}
        </label>
      )}
      <button
        className="c-custom-select__toggle"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="c-custom-select-dropdown"
        id={`custom-select-${id}`}
        aria-label={srLabel}
        onClick={(e) => {
          e.preventDefault();
          toggleDropdown();
        }}
        onKeyDown={(e) => {
          if (isOpen) {
            handleKeyDown(e);
          } else if (
            e.key === 'ArrowDown' ||
            e.key === 'Enter' ||
            e.key === ' '
          ) {
            e.preventDefault();
            setIsOpen(true);
            setHighlightedIndex(0);
          }
        }}
      >
        {selectedItem.value || defaultOption.value}

        <div
          aria-hidden
          className={classNames({
            'c-custom-select__chevron': true,
            'c-custom-select__chevron--is-expanded': isOpen,
          })}
        />
      </button>

      {isOpen && (
        <div className="c-custom-select__dropdown">
          <ul
            className="c-custom-select__content"
            role="listbox"
            id={`custom-select-${id}-dropdown`}
            aria-labelledby={`custom-select-${id}`}
            tabIndex={0}
            aria-activedescendant={`select-${id}-option-${highlightedIndex}`}
            onKeyDown={handleKeyDown}
            ref={dropdownRef}
            onBlur={() => {
              setIsOpen(false);
            }}
          >
            {options.map((option, index) => (
              <li
                className={classNames('c-custom-select__item', {
                  'c-custom-select__item--highlighted':
                    highlightedIndex === index,
                  'c-custom-select__item--selected':
                    selectedItem.key === option.key,
                })}
                key={option.key}
                id={`select-${id}-option-${index}`}
                role="option"
                aria-selected={selectedItem.key === option.key}
                onClick={() => [
                  onChange(option),
                  toggleDropdown(),
                  resetIndex(),
                ]}
              >
                <span>{option.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
